import React from "react";

const scenarioCards = [
  {
    iconBg: "bg-white border-white",
    icon: (
      <svg className="w-6 h-6 text-primary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    title: "Scaling Production Workloads",
    desc: "When RAG pipelines exceed current database capacity or query performance degrades under load, vector database migration enables horizontal scaling and improved throughput for production AI applications.",
  },
  {
    iconBg: "bg-primary-soft/50 border-primary-soft",
    icon: (
      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    title: "Infrastructure Flexibility",
    desc: "Migrating from managed to self-hosted vector databases or vice versa provides control over deployment architecture, data sovereignty, and compliance requirements for regulated industries.",
  },
  {
    iconBg: "bg-primary-soft/30 border-primary-soft",
    icon: (
      <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
      </svg>
    ),
    title: "Cost Optimisation",
    desc: "Vector database migration helps reduce operational expenses by moving to more cost-effective platforms, eliminating redundant storage, or optimising for specific usage patterns and query volumes.",
  },
  {
    iconBg: "bg-primary/50 border-primary-soft",
    icon: (
      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: "Performance Enhancement",
    desc: "Improving similarity search latency and retrieval accuracy through migration to vector databases optimised for specific embedding dimensions, distance metrics, or indexing algorithms.",
  },
];

const additionalTriggers = [
  "Upgrading to support new LLM architectures with different embedding dimensions",
  "Consolidating multiple vector databases into unified infrastructure",
  "Implementing disaster recovery and business continuity strategies",
  "Meeting new compliance requirements for data residency and governance",
  "Adopting advanced features like hybrid search or filtering capabilities",
];

export default function MigrationWhenSection() {
  return (
    <section id="when" className="mb-20 scroll-mt-20">

      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-12 bg-gradient-to-b from-white to-white/30 rounded-full" />
        <h2 className="text-4xl font-bold text-white">
          When Do You Need Vector Database Migration?
        </h2>
      </div>

      <div className="bg-primary-soft/30 border border-primary-soft rounded-2xl p-8 mb-8">
        <p className="text-white/70 text-lg leading-relaxed mb-6">
          Vector database migration becomes critical when your AI systems face scalability
          challenges, performance bottlenecks, or operational constraints. As organisations
          scale their RAG applications and LLM-powered systems, the ability to migrate vector
          embeddings efficiently between platforms ensures business continuity and technological
          flexibility.
        </p>
        <p className="text-white/70 text-lg leading-relaxed">
          Modern AI infrastructure requires agility. Whether you're optimising costs, improving
          retrieval accuracy, or avoiding vendor dependencies, understanding migration timing is
          essential for maintaining competitive advantage in AI deployment.
        </p>
      </div>

      <h3 className="text-2xl font-semibold mb-6 text-white">Critical Migration Scenarios</h3>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {scenarioCards.map((card, idx) => (
          <div
            key={idx}
            className="bg-primary-soft/20 border border-primary-soft rounded-xl p-6 hover:border-white transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg border flex items-center justify-center flex-shrink-0 ${card.iconBg}`}>
                {card.icon}
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-2 text-white">{card.title}</h4>
                <p className="text-white/60 leading-relaxed">{card.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-primary-soft/10 border border-white/20 rounded-xl p-6">
        <h4 className="font-semibold text-lg mb-3 text-white flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Additional Migration Triggers
        </h4>
        <ul className="space-y-2 text-white/70">
          {additionalTriggers.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="text-white mt-1">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}